import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import "./App.css";
import axios from "axios";

// ============================================
// EXTRACTED MODULES
// ============================================
import { API, BACKEND_URL } from './config';
import { apiClient, showToast } from './utils/apiClient';
import {
  formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek,
  formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert,
  formatDatetimeForInput, parseUTCDatetime, getTodayInAEST,
  isPrepaymentApproaching, hasAppointments, getAppointmentSummary
} from './utils/helpers';
import {
  getAvailableUrns, getAvailableKeepsakes, calculateBaseServicePrice,
  getProductPrice, getCustomProductIcon, getProductIcon,
  formatKeepsakesForDisplay, parseQuantityFromString, convertToCartFormat,
  calculateTotalCost
} from './utils/pricingHelpers';
import { AuthProvider, AuthContext, useAuth } from './context/AuthContext';
import { Router, RouterContext, useRouter } from './components/common/Router';
import PureSoulsLogin from './components/auth/PureSoulsLogin';
import ProfileRedirect from './components/auth/ProfileRedirect';
import LogoutButton from './components/auth/LogoutButton';
import { SearchableMultiSelect, SearchableSelect, VetClinicCombobox } from './components/forms/FormUtils';
import MultiItemSelector from './components/forms/MultiItemSelector';
import QRScannerModal from './components/qr/QRScannerModal';
import QuickScanViewModal from './components/qr/QuickScanViewModal';
import KeepsakesCollectionModal from './components/keepsakes/KeepsakesCollectionModal';
import SMSPreviewModal from './components/communications/SMSPreviewModal';
import EmailPreviewModal from './components/communications/EmailPreviewModal';
import AddAppointmentModal from './components/calendar/AddAppointmentModal';
import PendingTasksSection from './components/calendar/PendingTasksSection';
import CalendarView from './components/calendar/CalendarView';
import Dashboard from './components/dashboard/Dashboard';
import QuickLocationModal from './components/common/QuickLocationModal';
import OnlineSalesView from './components/online-sales/OnlineSalesView';
import ClinicRewardsView from './components/clinic-rewards/ClinicRewardsView';
import SimpleAnalyticsView from './components/analytics/SimpleAnalyticsView';
import PriorPetsDisplay from './components/pets/PriorPetsDisplay';
import PetView from './components/pets/PetView';
import { WorkflowQuickUpdate } from './components/workflow/WorkflowQuickUpdate';
import { QuickLocationUpdateButton } from './components/workflow/QuickLocationUpdateButton';
import WorkflowModal from './components/workflow/WorkflowModal';
import AddPetModal from './components/pets/AddPetModal';
import PetEdit from './components/pets/PetEdit';
import WalkInFormModal from './components/pets/WalkInFormModal';
import ThankYouModal from './components/common/ThankYouModal';
import OnlineSalesModal from './components/online-sales/OnlineSalesModal';
import {
  CustomerInfoStep, ProductCatalogStep, CartReviewStep,
  CheckoutStep, OrderSuccessStep
} from './components/online-sales/CustomerInfoStep';
import OrderHistoryModal from './components/online-sales/OrderHistoryModal';
import OrderDetailsModal from './components/online-sales/OrderDetailsModal';

// Load Google Maps API on init
const loadGoogleMapsAPI = () => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey || (window.google && window.google.maps)) return;
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&region=AU&loading=async`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
};
if (typeof window !== 'undefined') loadGoogleMapsAPI();

// Register service worker for push notifications on app startup
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

function App() {
  const { user, loading } = useAuth();
  const { currentPath } = useRouter();

  // Remove Emergent badge if it gets injected dynamically
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        console.log('🗑️ Removing Emergent badge');
        badge.remove();
      }
      // Also remove any elements with Emergent-related content
      const badges = document.querySelectorAll('[href*="emergent"], [href*="Emergent"]');
      badges.forEach(element => {
        if (element.textContent && element.textContent.includes('Made with Emergent')) {
          console.log('🗑️ Removing Emergent link');
          element.remove();
        }
      });
    };
    
    // Run immediately and then periodically
    removeBadge();
    const interval = setInterval(removeBadge, 1000);
    
    // Also run on DOM changes
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if we're on the profile redirect route
  if (currentPath === '/profile' || currentPath.startsWith('/profile#')) {
    return <ProfileRedirect />;
  }

  // HOLONS Staff Authentication
  if (!user) {
    return <PureSoulsLogin />;
  }

  // Route handling for authenticated users
  if (currentPath.startsWith('/pets/') && currentPath.endsWith('/edit')) {
    const petId = currentPath.split('/')[2];
    return <PetEdit petId={petId} />;
  }

  if (currentPath.startsWith('/pets/') && !currentPath.includes('/edit')) {
    const petId = currentPath.split('/')[2];
    return <PetView petId={petId} />;
  }

  return <Dashboard />;
}

function AppWithAuthAndRouter() {
  return (
    <AuthProvider>
      <Router>
        <App />
      </Router>
    </AuthProvider>
  );
}

export default AppWithAuthAndRouter;
